import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { Calendar, Users, AlertTriangle, Ban, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type { EventOccurrenceWithBookings } from '@/types/events';

interface EventOccurrencesListProps {
  occurrences: EventOccurrenceWithBookings[];
  maxSpots: number;
  onCancelOccurrence?: (occurrenceId: string) => Promise<void>;
  isCancelling?: boolean;
}

export function EventOccurrencesList({
  occurrences,
  maxSpots,
  onCancelOccurrence,
  isCancelling = false,
}: EventOccurrencesListProps) {
  if (occurrences.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>Inga tillfällen skapade ännu</p>
          <p className="text-sm mt-1">Lägg till datum i editorn för att skapa tillfällen</p>
        </CardContent>
      </Card>
    );
  }

  const scheduledCount = occurrences.filter(o => o.status === 'scheduled').length;
  const cancelledCount = occurrences.filter(o => o.status === 'cancelled').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span>{scheduledCount} planerade</span>
        {cancelledCount > 0 && (
          <span className="text-destructive">{cancelledCount} inställda</span>
        )}
      </div>

      <div className="space-y-2">
        {occurrences.map((occ) => {
          const spotsLeft = (occ.capacity ?? maxSpots) - occ.booked_spots;
          const isCancelled = occ.status === 'cancelled';
          const isPast = new Date(occ.start_at) < new Date();

          return (
            <Card
              key={occ.id}
              className={cn(
                "transition-opacity",
                (isCancelled || isPast) && "opacity-60"
              )}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {format(new Date(occ.start_at), "EEEE d MMMM yyyy", { locale: sv })}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(occ.start_at), "HH:mm")} - {format(new Date(occ.end_at), "HH:mm")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {isCancelled ? (
                      <Badge variant="destructive" className="text-xs">
                        Inställd
                      </Badge>
                    ) : isPast ? (
                      <Badge variant="secondary" className="text-xs">
                        Passerad
                      </Badge>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Booking stats */}
                  <div className="flex items-center gap-1 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {occ.booked_spots} / {occ.capacity ?? maxSpots}
                    </span>
                  </div>

                  {/* Cancel button */}
                  {!isCancelled && !isPast && onCancelOccurrence && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={isCancelling}
                        >
                          {isCancelling ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Ban className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Ställa in tillfälle?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {occ.booked_spots > 0 ? (
                              <>
                                <span className="flex items-center gap-2 text-amber-600 mb-2">
                                  <AlertTriangle className="h-4 w-4" />
                                  Detta tillfälle har {occ.booked_spots} bokningar
                                </span>
                                Du bör manuellt kontakta de bokade gästerna för att informera om ändringen.
                              </>
                            ) : (
                              'Detta tillfälle har inga bokningar och kommer markeras som inställt.'
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Avbryt</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onCancelOccurrence(occ.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Ställ in tillfälle
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
